import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MixedText } from '../utils/rtl';
import {
  BookOpen,
  Upload,
  Brain,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  ArrowLeft,
  Download,
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  Play,
  Chrome,
  Zap,
  Clock,
  FileText,
  Plus,
  MessageCircle,
  Send
} from 'lucide-react';
import { analytics } from '../services/analytics';
import { useOnboarding } from '../hooks/useOnboarding';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';

interface OnboardingPageProps {
  onComplete?: () => void;
}

type OnboardingStep = 'welcome' | 'overview' | 'chat' | 'zoom';

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  const { markOnboardingComplete } = useOnboarding();
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const steps: OnboardingStep[] = ['welcome', 'overview', 'chat', 'zoom'];
  const currentStepIndex = steps.indexOf(currentStep);

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    } else {
      // Complete onboarding and redirect to dashboard
      handleComplete();
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleComplete = () => {
    console.debug('[OnboardingPage] Completing onboarding');
    markOnboardingComplete();
    analytics.track('Onboarding Completed', {
      completed_step: currentStep,
      total_steps: steps.length
    });
      if (onComplete) {
        onComplete();
      }
    navigate('/');
  };

  const handleSkip = () => {
    console.debug('[OnboardingPage] Skipping onboarding');
    markOnboardingComplete();
    analytics.track('Onboarding Skipped', {
      skipped_at_step: currentStep,
      step_index: currentStepIndex
    });
    if (onComplete) {
      onComplete();
    }
    navigate('/');
  };

  const openZoomEasyDownload = () => {
    window.open('https://chromewebstore.google.com/detail/zed-zoom-easy-downloader/pdadlkbckhinonakkfkdaadceojbekep', '_blank');
  };

  const renderWelcomeStep = () => (
    <div className="text-center py-8">
      <div className="w-32 h-32 mx-auto mb-6">
        <img 
          src="/astronaut_cool.png" 
          alt="Bob the StudyBel Astronaut" 
          className="w-full h-full object-contain filter brightness-110"
        />
      </div>
      
      <h2 className="text-3xl font-bold text-bg-primary mb-4 font-hebrew text-center">
        {isRTL ? (
          <MixedText>{t('onboarding:welcome.title')}</MixedText>
        ) : (
          t('onboarding:welcome.title')
        )}
      </h2>
      
      <p className="text-lg text-bg-secondary mb-8 max-w-md mx-auto font-hebrew text-center">
        {isRTL ? (
          <MixedText>{t('onboarding:welcome.subtitle')}</MixedText>
        ) : (
          t('onboarding:welcome.subtitle')
        )}
      </p>
      
      <Button
        onClick={nextStep}
        variant="primary"
        size="lg"
        className={`px-8 py-3 text-lg font-hebrew ${isRTL ? 'flex-row-reverse' : ''} inline-flex items-center`}
      >
        {isRTL ? (
          <>
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('onboarding:welcome.getStarted')}
          </>
        ) : (
          <>
            {t('onboarding:welcome.getStarted')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );

  const renderOverviewStep = () => (
    <div className="py-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center">
          {isRTL ? (
            <MixedText>{t('onboarding:steps.overview.title')}</MixedText>
          ) : (
            t('onboarding:steps.overview.title')
          )}
        </h2>
        <div className={`${isRTL ? 'flex justify-end' : ''}`}>
          <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {isRTL ? (
              <MixedText>{t('onboarding:steps.overview.description')}</MixedText>
            ) : (
              t('onboarding:steps.overview.description')
            )}
          </p>
        </div>
      </div>

      {/* Quick Actions Grid - Non-clickable version */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-4 max-w-4xl mx-auto">
        {isRTL ? (
          <>
            {/* Summarize Document - Top Right in RTL */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-vibrant-orange/60 to-vibrant-orange/70 
                backdrop-blur-sm border border-vibrant-orange/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_reading.png" 
                alt="StudyBel Astronaut Reading" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.summarize_document')}</MixedText>
              </span>
            </div>

            {/* Summarize Lecture - Top Left in RTL */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-vibrant-orange/60 to-vibrant-orange/70 
                backdrop-blur-sm border border-vibrant-orange/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_video.png" 
                alt="StudyBel Astronaut Video" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.summarize_lecture')}</MixedText>
              </span>
            </div>

            {/* Generate Exam - Bottom Right in RTL */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-muted-purple/60 to-deep-space-purple/70 
                backdrop-blur-sm border border-muted-purple/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_idea.png" 
                alt="StudyBel Astronaut Idea" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.generate_exam')}</MixedText>
              </span>
            </div>

            {/* Solve Exam - Bottom Left in RTL */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-muted-purple/60 to-deep-space-purple/70 
                backdrop-blur-sm border border-muted-purple/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_checkbox.png" 
                alt="StudyBel Astronaut Checkbox" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.solve_exam')}</MixedText>
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Summarize Lecture - Top Left in LTR */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-vibrant-orange/60 to-vibrant-orange/70 
                backdrop-blur-sm border border-vibrant-orange/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_video.png" 
                alt="StudyBel Astronaut Video" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.summarize_lecture')}</MixedText>
              </span>
            </div>

            {/* Summarize Document - Top Right in LTR */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-vibrant-orange/60 to-vibrant-orange/70 
                backdrop-blur-sm border border-vibrant-orange/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_reading.png" 
                alt="StudyBel Astronaut Reading" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.summarize_document')}</MixedText>
              </span>
            </div>

            {/* Solve Exam - Bottom Left in LTR */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-muted-purple/60 to-deep-space-purple/70 
                backdrop-blur-sm border border-muted-purple/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_checkbox.png" 
                alt="StudyBel Astronaut Checkbox" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.solve_exam')}</MixedText>
              </span>
            </div>

            {/* Generate Exam - Bottom Right in LTR */}
            <div
              className={`p-4 rounded-2xl font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                bg-gradient-to-br from-muted-purple/60 to-deep-space-purple/70 
                backdrop-blur-sm border border-muted-purple/30 
                shadow-lg cursor-default
                text-astronaut-white opacity-80`}
            >
              <img 
                src="/astronaut_idea.png" 
                alt="StudyBel Astronaut Idea" 
                className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
              />
              <span className="text-lg md:text-xl">
                <MixedText>{t('dashboard:quick_actions.generate_exam')}</MixedText>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderChatStep = () => (
    <div className="py-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center">
          <MixedText>{t('onboarding:steps.chat.title')}</MixedText>
        </h2>
        <div className={`${isRTL ? 'flex justify-end' : ''}`}>
          <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {isRTL ? (
              <MixedText>{t('onboarding:steps.chat.description')}</MixedText>
            ) : (
              t('onboarding:steps.chat.description')
            )}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Chat Showcase - White card with proper RTL */}
        <div className="chat-card bg-white rounded-2xl border border-surface-muted/30 overflow-hidden shadow-lg">
          <div className={`chat-header p-6 border-b border-surface-muted/20 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className={`flex items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-16 h-16 bg-gradient-to-br from-muted-purple to-muted-purple/80 rounded-2xl flex items-center justify-center shadow-lg ${isRTL ? 'ml-4' : 'mr-4'}`}>
                <img 
                  src="/astronaut_cool.png" 
                  alt="Bob" 
                  className="w-12 h-12 object-contain filter brightness-110"
                />
              </div>
              <div className="flex-1">
                <h3 className={`text-xl font-bold text-surface-primary font-hebrew mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.chat.feature_title')}</MixedText>
                  ) : (
                    t('onboarding:steps.chat.feature_title')
                  )}
                </h3>
                <p className={`text-muted-purple font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.chat.feature_description')}</MixedText>
                  ) : (
                    t('onboarding:steps.chat.feature_description')
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Chat Demo Messages */}
          <div className="chat-demo p-6 space-y-4">
            {/* User Message */}
            <div className={`chat-message user flex ${isRTL ? 'justify-end' : 'justify-end'}`}>
              <div className={`message-bubble bg-vibrant-orange/20 rounded-2xl px-4 py-3 max-w-xs ${isRTL ? 'rounded-bl-md' : 'rounded-br-md'}`}>
                <p className={`text-sm text-surface-primary font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.chat.sample_user_message')}</MixedText>
                  ) : (
                    t('onboarding:steps.chat.sample_user_message')
                  )}
                </p>
              </div>
            </div>

            {/* AI Response */}
            <div className={`chat-message ai flex items-start justify-start`}>
              <div className={`message-avatar w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ${isRTL ? 'order-1 ml-3' : 'order-1 mr-3'}`}>
                <img 
                  src="/astronaut_cool.png" 
                  alt="Bob" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`message-bubble bg-muted-purple/20 rounded-2xl px-4 py-3 max-w-sm ${isRTL ? 'order-2 rounded-br-md' : 'order-2 rounded-bl-md'}`}>
                <p className={`text-sm text-surface-primary font-hebrew leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.chat.sample_bob_response')}</MixedText>
                  ) : (
                    t('onboarding:steps.chat.sample_bob_response')
                  )}
                </p>
              </div>
            </div>

            {/* Follow-up User Message */}
            <div className={`chat-message user flex ${isRTL ? 'justify-end' : 'justify-end'}`}>
              <div className={`message-bubble bg-vibrant-orange/20 rounded-2xl px-4 py-3 max-w-xs ${isRTL ? 'rounded-bl-md' : 'rounded-br-md'}`}>
                <p className={`text-sm text-surface-primary font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.chat.sample_followup_message')}</MixedText>
                  ) : (
                    t('onboarding:steps.chat.sample_followup_message')
                  )}
                </p>
              </div>
            </div>

            {/* Typing Indicator */}
            <div className={`chat-message ai flex items-start justify-start`}>
              <div className={`message-avatar w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ${isRTL ? 'order-1 ml-3' : 'order-1 mr-3'}`}>
                <img 
                  src="/astronaut_cool.png" 
                  alt="Bob" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`message-bubble bg-muted-purple/10 rounded-2xl px-4 py-3 ${isRTL ? 'order-2 rounded-br-md' : 'order-2 rounded-bl-md'}`}>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                  <div className="w-2 h-2 bg-muted-purple/60 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-muted-purple/60 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-muted-purple/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Input Area */}
          <div className="border-t border-surface-muted/20 p-4">
            <div className={`flex items-center space-x-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <input
                type="text"
                placeholder={t('onboarding:steps.chat.input_placeholder')}
                className={`flex-1 px-4 py-3 border border-surface-muted/30 rounded-xl bg-surface-muted/5 text-sm font-hebrew transition-all duration-200 focus:border-muted-purple/50 focus:ring-2 focus:ring-muted-purple/20 ${isRTL ? 'text-right' : 'text-left'}`}
                disabled
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <button
                className="p-3 mr-3 ml-3 bg-gradient-to-br from-muted-purple to-muted-purple/80 rounded-xl transition-all duration-200 cursor-default opacity-70 shadow-lg"
                disabled
              >
                <Send className="w-5 h-5 text-astronaut-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderZoomStep = () => (
    <div className="py-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center">
          {isRTL ? (
            <MixedText>{t('onboarding:steps.zoom.title')}</MixedText>
          ) : (
            t('onboarding:steps.zoom.title')
          )}
        </h2>
        <div className={`${isRTL ? 'flex justify-end' : ''}`}>
          <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {isRTL ? (
              <MixedText>{t('onboarding:steps.zoom.description')}</MixedText>
            ) : (
              t('onboarding:steps.zoom.description')
            )}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Chrome Extension - Primary Method */}
        <SpaceCard constellation="ursa" className="group hover:shadow-xl transition-all duration-300 border-2 border-vibrant-orange/30">
          <CardContent className="p-8">
            <div className={`flex items-center mb-6 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
              <div className={`w-16 h-16 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/80 rounded-2xl flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'} shadow-lg`}>
                <Chrome className="w-8 h-8 text-astronaut-white" />
              </div>
              <div className="flex-1">
                <h3 className={`text-2xl font-bold text-surface-primary font-hebrew mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.zoom.extension.title')}</MixedText>
                  ) : (
                    t('onboarding:steps.zoom.extension.title')
                  )}
                </h3>
                <p className={`text-vibrant-orange font-semibold font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? (
                    <MixedText>{t('onboarding:steps.zoom.extension.description')}</MixedText>
                  ) : (
                    t('onboarding:steps.zoom.extension.description')
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {(t('onboarding:steps.zoom.extension.steps', { returnObjects: true }) as string[]).map((step: string, index: number) => (
                <div key={index} className={`flex items-start ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <span className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/80 text-astronaut-white rounded-full flex items-center justify-center text-sm font-bold ${isRTL ? 'ml-4' : 'mr-4'} shadow-sm`}>
                    {index + 1}
                  </span>
                  <span className={`text-surface-primary font-hebrew leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                    {isRTL ? (
                      <MixedText>{step}</MixedText>
                    ) : (
                      step
                    )}
                  </span>
                </div>
              ))}
            </div>

            <Button
              onClick={openZoomEasyDownload}
              variant="primary"
              size="lg"
              className={`shadow-lg hover:shadow-xl transition-all duration-200 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ExternalLink className={`w-5 h-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
              {isRTL ? (
                <MixedText>{t('onboarding:steps.zoom.extension.installButton')}</MixedText>
              ) : (
                t('onboarding:steps.zoom.extension.installButton')
              )}
            </Button>
          </CardContent>
        </SpaceCard>

        {/* Essential Info */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className={`text-center text-sm text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
              <span className="font-semibold">
                {isRTL ? (
                  <MixedText>תומך בקבצי: MP4, MP3, WAV, M4A</MixedText>
                ) : (
                  "Supports: MP4, MP3, WAV, M4A files"
                )}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return renderWelcomeStep();
      case 'overview':
        return renderOverviewStep();
      case 'chat':
        return renderChatStep();
      case 'zoom':
        return renderZoomStep();
      default:
        return renderWelcomeStep();
    }
  };

  return (
    <DashboardContainer>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className={`transform transition-all duration-500 ${
          showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}>
          {/* Progress indicator */}
          {currentStep !== 'welcome' && (
            <div className="mb-8 max-w-4xl mx-auto">
              <div className={`flex items-center justify-between text-sm text-bg-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="font-hebrew">
                  {currentStepIndex} / {steps.length - 1}
                </span>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-surface-muted/30 rounded-full h-2">
                    <div
                      className={`bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 h-2 rounded-full transition-all duration-300 ${isRTL ? 'float-right' : ''}`}
                      style={{ 
                        width: `${((currentStepIndex) / (steps.length - 1)) * 100}%`,
                        transformOrigin: isRTL ? 'right' : 'left'
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="max-w-4xl mx-auto">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          {currentStep !== 'welcome' && currentStep !== 'zoom' && (
            <div className={`mt-8 max-w-4xl mx-auto flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                onClick={prevStep}
                variant="secondary"
                className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                disabled={currentStepIndex === 1}
              >
                {isRTL ? (
                  <>
                    <ChevronRight className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.previous')}
                  </>
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.previous')}
                  </>
                )}
              </Button>

              <Button
                onClick={nextStep}
                variant="primary"
                className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                {isRTL ? (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.next')}
                  </>
                ) : (
                  <>
                    {t('onboarding:navigation.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Zoom step navigation */}
          {currentStep === 'zoom' && (
            <div className={`mt-8 max-w-4xl mx-auto flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                onClick={prevStep}
                variant="secondary"
                className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                {isRTL ? (
                  <>
                    <ChevronRight className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.previous')}
                  </>
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.previous')}
                  </>
                )}
              </Button>

              <Button
                onClick={handleComplete}
                variant="primary"
                className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''} font-hebrew`}
              >
                {isRTL ? (
                  <>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    {t('onboarding:navigation.get_started')}
                  </>
                ) : (
                  <>
                    {t('onboarding:navigation.get_started')}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};

export default OnboardingPage; 
